/**
 * AI Form Optimization Modal
 * 
 * Shows AI-generated suggestions for optimizing an existing form.
 * Users can:
 * - Select a preset optimization goal
 * - Customize with their own criteria
 * - Accept/reject each suggestion individually
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, Sparkles, Check, X, ArrowRight, AlertCircle, RefreshCw, Target, DollarSign, Clock, Filter, Palette } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";

// Preset icons mapping
const PRESET_ICONS = {
  high_value_clients: DollarSign,
  urgent_projects: Clock,
  best_fit_leads: Target,
  filter_low_quality: Filter,
  creative_evaluation: Palette
};

export default function AIFormOptimizeModal({
  isOpen,
  onClose,
  form,
  token,
  onApplySuggestions
}) {
  const [step, setStep] = useState("preset"); // preset, loading, suggestions
  const [presets, setPresets] = useState({});
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [customCriteria, setCustomCriteria] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [summary, setSummary] = useState("");
  const [presetApplied, setPresetApplied] = useState(null);
  const [acceptedSuggestions, setAcceptedSuggestions] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingPresets, setLoadingPresets] = useState(true);

  // Fetch presets on mount
  useEffect(() => {
    if (isOpen) {
      fetchPresets();
    }
  }, [isOpen]);

  // Update default text when preset changes
  useEffect(() => {
    if (selectedPreset && presets[selectedPreset]) {
      setCustomCriteria(presets[selectedPreset].default_text);
    }
  }, [selectedPreset, presets]);

  const fetchPresets = async () => {
    setLoadingPresets(true);
    try {
      const response = await axios.get(`${API}/forms/ai/presets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPresets(response.data.presets || {});
    } catch (error) {
      console.error("Error fetching presets:", error);
      toast.error("Failed to load optimization presets");
    } finally {
      setLoadingPresets(false);
    }
  };

  const handleOptimize = async () => {
    if (!selectedPreset && !customCriteria.trim()) {
      toast.error("Please select a goal or describe your criteria");
      return;
    }
    
    setLoading(true);
    setStep("loading");
    
    try {
      const response = await axios.post(
        `${API}/forms/ai/optimize`,
        {
          form_id: form.id,
          preset: selectedPreset,
          custom_criteria: customCriteria
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setSuggestions(response.data.suggestions || []);
      setSummary(response.data.summary || "");
      setPresetApplied(response.data.preset_applied);
      
      // Initialize all suggestions as not accepted
      const initial = {};
      response.data.suggestions?.forEach((_, idx) => {
        initial[idx] = false;
      });
      setAcceptedSuggestions(initial);
      
      setStep("suggestions");
    } catch (error) {
      console.error("AI optimization error:", error);
      toast.error(error.response?.data?.detail || "Failed to optimize form. Please try again.");
      setStep("preset");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSuggestion = (idx) => {
    setAcceptedSuggestions(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  const handleAcceptAll = () => {
    const all = {};
    suggestions.forEach((_, idx) => {
      all[idx] = true;
    });
    setAcceptedSuggestions(all);
  };

  const handleRejectAll = () => {
    const all = {};
    suggestions.forEach((_, idx) => {
      all[idx] = false;
    });
    setAcceptedSuggestions(all);
  };

  const handleApply = () => {
    const acceptedChanges = suggestions.filter((_, idx) => acceptedSuggestions[idx]);
    
    if (acceptedChanges.length === 0) {
      toast.error("No suggestions selected. Please accept at least one change.");
      return;
    }
    
    onApplySuggestions(acceptedChanges, presetApplied);
    onClose();
    toast.success(`Applied ${acceptedChanges.length} AI suggestions`);
  };

  const handleClose = () => {
    setStep("preset");
    setSelectedPreset(null);
    setCustomCriteria("");
    setSuggestions([]);
    setAcceptedSuggestions({});
    onClose();
  };

  const getConfidenceBadge = (confidence) => {
    const colors = {
      high: "bg-green-100 text-green-800",
      medium: "bg-yellow-100 text-yellow-800",
      low: "bg-red-100 text-red-800"
    };
    return colors[confidence] || colors.medium;
  };

  const acceptedCount = Object.values(acceptedSuggestions).filter(Boolean).length;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-500" />
            Optimize Form with AI
          </DialogTitle>
          <DialogDescription>
            {step === "preset" && "Select an optimization goal to guide AI suggestions."}
            {step === "loading" && "Analyzing your form..."}
            {step === "suggestions" && "Review and apply AI suggestions to improve your form."}
          </DialogDescription>
        </DialogHeader>

        {step === "preset" && (
          <div className="space-y-6 py-4">
            {loadingPresets ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
              </div>
            ) : (
              <>
                {/* Preset Selection */}
                <div>
                  <Label className="mb-3 block font-medium">What do you want to optimize for?</Label>
                  <RadioGroup 
                    value={selectedPreset || ""} 
                    onValueChange={setSelectedPreset}
                    className="space-y-2"
                  >
                    {Object.entries(presets).map(([key, preset]) => {
                      const Icon = PRESET_ICONS[key] || Target;
                      return (
                        <div 
                          key={key}
                          className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedPreset === key 
                              ? "border-purple-500 bg-purple-50" 
                              : "border-gray-200 hover:border-purple-300"
                          }`}
                          onClick={() => setSelectedPreset(key)}
                        >
                          <RadioGroupItem value={key} id={key} className="mt-1" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4 text-purple-600" />
                              <label htmlFor={key} className="font-medium cursor-pointer">
                                {preset.name}
                              </label>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {preset.description}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </RadioGroup>
                </div>

                {/* Custom Criteria */}
                <div>
                  <Label htmlFor="criteria" className="mb-2 block">
                    Describe what makes a strong lead for you (optional refinement)
                  </Label>
                  <Textarea
                    id="criteria"
                    placeholder="e.g., 'We only want clients with budgets over $10k who need delivery within 2 weeks'"
                    value={customCriteria}
                    onChange={(e) => setCustomCriteria(e.target.value)}
                    className="mt-1"
                    rows={3}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {selectedPreset 
                      ? "This text was auto-filled from your selection. Feel free to customize it."
                      : "Describe your ideal lead to help AI tailor suggestions."
                    }
                  </p>
                </div>
              </>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button 
                onClick={handleOptimize} 
                disabled={loading || loadingPresets || (!selectedPreset && !customCriteria.trim())}
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Analyze & Suggest
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "loading" && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-purple-500 mb-4" />
            <p className="text-muted-foreground">Optimizing for {presets[selectedPreset]?.name || "your criteria"}...</p>
            <p className="text-xs text-muted-foreground mt-2">This may take a few seconds</p>
          </div>
        )}

        {step === "suggestions" && (
          <div className="space-y-4 py-2">
            {presetApplied && presets[presetApplied] && (
              <div className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg border border-purple-200">
                <Sparkles className="h-4 w-4 text-purple-600" />
                <span className="text-sm text-purple-800">
                  Optimized for: <strong>{presets[presetApplied].name}</strong>
                </span>
              </div>
            )}

            {summary && (
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">{summary}</p>
              </div>
            )}

            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {acceptedCount} of {suggestions.length} suggestions selected
              </p>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={handleAcceptAll}>
                  Accept All
                </Button>
                <Button variant="ghost" size="sm" onClick={handleRejectAll}>
                  Reject All
                </Button>
              </div>
            </div>

            <div className="space-y-3 max-h-[40vh] overflow-y-auto">
              {suggestions.map((suggestion, idx) => (
                <Card 
                  key={idx}
                  className={`cursor-pointer transition-all ${
                    acceptedSuggestions[idx] 
                      ? "border-green-500 bg-green-50/50" 
                      : "hover:border-gray-400"
                  }`}
                  onClick={() => handleToggleSuggestion(idx)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm text-muted-foreground line-through">
                            {suggestion.original_label}
                          </span>
                          <ArrowRight className="h-3 w-3 text-muted-foreground" />
                          <span className="text-sm font-medium">
                            {suggestion.suggested_label}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2 flex-wrap">
                          {suggestion.include_in_score && (
                            <Badge className="bg-blue-100 text-blue-800">
                              Scoring: {suggestion.score_weight}%
                            </Badge>
                          )}
                          <Badge className={getConfidenceBadge(suggestion.confidence)}>
                            {suggestion.confidence} confidence
                          </Badge>
                        </div>
                        
                        {suggestion.reason && (
                          <p className="text-xs text-muted-foreground">
                            {suggestion.reason}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex items-center">
                        {acceptedSuggestions[idx] ? (
                          <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                            <Check className="h-4 w-4 text-white" />
                          </div>
                        ) : (
                          <div className="w-8 h-8 rounded-full border-2 border-gray-300" />
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {suggestions.length === 0 && (
              <div className="text-center py-8">
                <AlertCircle className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-muted-foreground">No suggestions available</p>
              </div>
            )}

            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setStep("preset")}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button onClick={handleApply} disabled={acceptedCount === 0}>
                <Check className="h-4 w-4 mr-2" />
                Apply {acceptedCount} Changes
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
